import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { format, addDays, isSameDay } from "date-fns";
import { it } from "date-fns/locale";
import { CalendarDays, Clock, User, CheckCircle2, Loader2, Scissors } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const DAY_COUNT = 60;

const endTime = (time, duration) => {
  const [h, m] = time.split(":").map(Number);
  const t = h * 60 + m + duration;
  return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
};

export function Booking({ services, selectedService, setSelectedService }) {
  const days = useMemo(() => Array.from({ length: DAY_COUNT }, (_, i) => addDays(new Date(), i)), []);
  const [selectedDay, setSelectedDay] = useState(() => {
    const d = days.find((x) => x.getDay() !== 0 && x.getDay() !== 1);
    return d || days[0];
  });
  const [slots, setSlots] = useState([]);
  const [closed, setClosed] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const dateStr = format(selectedDay, "yyyy-MM-dd");
  const service = services.find((s) => s.id === selectedService);

  const loadSlots = async (silent = false) => {
    if (!selectedService) return;
    if (!silent) setLoadingSlots(true);
    try {
      const { data } = await api.get("/availability", { params: { date: dateStr, service: selectedService } });
      setSlots(data.slots.filter((s) => s.available));
      setClosed(data.closed);
    } catch {
      if (!silent) toast.error("Impossibile caricare le disponibilità. Riprova.");
    } finally {
      setLoadingSlots(false);
    }
  };

  useEffect(() => {
    setSelectedTime(null);
    loadSlots();
    const t = setInterval(() => loadSlots(true), 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateStr, selectedService]);

  const isClosedDay = (d) => d.getDay() === 0 || d.getDay() === 1;

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedTime) {
      toast.error("Scegli prima un orario di inizio.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        notes: form.notes.trim() || null,
        service: selectedService,
        date: dateStr,
        time: selectedTime,
      });
      setConfirmation(data);
      toast.success("Prenotazione confermata. Ti aspettiamo in bottega!");
      loadSlots(true);
    } catch (err) {
      toast.error(formatApiError(err, "Prenotazione non riuscita. Riprova."));
      loadSlots(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="prenota" className="relative py-24 sm:py-36 bg-espresso/40 border-y border-white/[0.06] overflow-hidden">
      <div className="absolute top-0 left-1/4 w-[500px] h-[400px] bg-ember/[0.07] blur-[130px] rounded-full pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="text-center max-w-2xl mx-auto"
        >
          <p className="text-xs font-mono tracking-[0.3em] uppercase text-ember">Prenotazione Online</p>
          <h2 className="mt-4 font-serif font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-ivory">
            Riserva il tuo <span className="italic text-ember">momento.</span>
          </h2>
          <p className="mt-4 text-sm sm:text-base text-sand leading-relaxed">
            Scegli il servizio, il giorno e l'orario di inizio: ogni servizio occupa solo il tempo che serve.
            Le disponibilità si aggiornano in tempo reale.
          </p>
        </motion.div>

        <AnimatePresence mode="wait">
          {confirmation ? (
            <motion.div
              key="confirm"
              data-testid="booking-confirmation-modal"
              initial={{ opacity: 0, scale: 0.96, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="mt-14 max-w-xl mx-auto bg-card border border-ember/30 rounded-lg p-8 sm:p-10 text-center shadow-[0_0_60px_rgba(230,90,30,0.12)]"
            >
              <CheckCircle2 size={44} className="mx-auto text-ember" />
              <h3 className="mt-5 font-serif text-3xl text-ivory">Ci vediamo in bottega, {confirmation.name.split(" ")[0]}.</h3>
              <p className="mt-2 text-sm text-sand">La tua fascia è riservata. Ti invieremo un promemoria via email il giorno prima.</p>

              <div className="mt-8 bg-espresso border border-white/[0.08] rounded-md divide-y divide-white/[0.06] text-left">
                {[
                  ["Servizio", confirmation.service_name],
                  ["Giorno", format(new Date(confirmation.date + "T00:00:00"), "EEEE d MMMM yyyy", { locale: it })],
                  ["Orario", `${confirmation.time} — ${endTime(confirmation.time, confirmation.duration || 60)} (${confirmation.duration || 60} min)`],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between items-center px-5 py-3.5">
                    <span className="text-xs font-mono uppercase tracking-widest text-stone">{k}</span>
                    <span className="text-sm text-ivory capitalize">{v}</span>
                  </div>
                ))}
                <div className="flex justify-between items-center px-5 py-3.5">
                  <span className="text-xs font-mono uppercase tracking-widest text-stone">Codice</span>
                  <span data-testid="booking-confirmation-code" className="text-sm font-mono text-ember tracking-widest">
                    {confirmation.id.slice(0, 8).toUpperCase()}
                  </span>
                </div>
              </div>

              <p className="mt-6 text-xs text-stone leading-relaxed">
                Riceverai subito un'email di conferma con il pulsante per annullare in caso di imprevisti.
                In alternativa contatta Daniele al{" "}
                <a href="tel:+393273832605" className="text-ember hover:text-ember-hover">+39 327 383 2605</a>
                {" "}o scrivendo a{" "}
                <a href="mailto:danieledesanctis772@gmail.com" className="text-ember hover:text-ember-hover">
                  danieledesanctis772@gmail.com
                </a>
                : lo slot tornerà subito disponibile.
              </p>

              <button
                data-testid="booking-new-button"
                onClick={() => {
                  setConfirmation(null);
                  setSelectedTime(null);
                  setForm({ name: "", phone: "", email: "", notes: "" });
                }}
                className="mt-8 px-6 py-3 border border-white/15 hover:border-ember/50 text-sand hover:text-ivory text-xs font-mono uppercase tracking-widest rounded-sm transition-colors duration-300"
              >
                Nuova prenotazione
              </button>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.6 }}
              className="mt-14"
            >
              <div className="bg-card border border-white/[0.08] rounded-lg p-6">
                <div className="flex items-center gap-2 text-ember">
                  <Scissors size={16} />
                  <span className="text-xs font-mono uppercase tracking-[0.25em]">1 · Il servizio</span>
                </div>
                <div data-testid="booking-select-service" className="mt-5 flex flex-wrap gap-2">
                  {services.map((s) => (
                    <button
                      type="button"
                      key={s.id}
                      data-testid={`booking-service-${s.id.replace(/_/g, "-")}`}
                      onClick={() => setSelectedService(s.id)}
                      className={`rounded-full border px-4 py-2 text-xs font-mono tracking-wide transition-all duration-300 ${
                        selectedService === s.id
                          ? "bg-ember border-ember text-white"
                          : "border-white/10 text-sand hover:border-ember/50 hover:text-ivory"
                      }`}
                    >
                      {s.name} · {s.price} · {s.duration}min
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-6 grid lg:grid-cols-12 gap-6">
                <div className="lg:col-span-4 bg-card border border-white/[0.08] rounded-lg p-6">
                  <div className="flex items-center gap-2 text-ember">
                    <CalendarDays size={16} />
                    <span className="text-xs font-mono uppercase tracking-[0.25em]">2 · Il giorno</span>
                  </div>
                  <div data-testid="booking-date-picker" className="mt-5 grid grid-cols-3 gap-2 max-h-[340px] overflow-y-auto no-scrollbar">
                    {days.map((d) => {
                      const closedDay = isClosedDay(d);
                      const active = isSameDay(d, selectedDay);
                      return (
                        <button
                          key={d.toISOString()}
                          data-testid={`booking-day-${format(d, "yyyy-MM-dd")}`}
                          disabled={closedDay}
                          onClick={() => setSelectedDay(d)}
                          className={`rounded-md border px-2 py-3 text-center transition-all duration-300 ${
                            active
                              ? "bg-ember border-ember text-white shadow-[0_0_18px_rgba(230,90,30,0.35)]"
                              : closedDay
                                ? "border-white/[0.05] text-stone/40 cursor-not-allowed"
                                : "border-white/10 text-sand hover:border-ember/50 hover:text-ivory"
                          }`}
                        >
                          <span className="block text-[10px] font-mono uppercase tracking-widest">
                            {format(d, "EEE", { locale: it })}
                          </span>
                          <span className="block font-serif text-xl leading-tight">{format(d, "d")}</span>
                          <span className="block text-[10px] font-mono uppercase tracking-widest opacity-70">
                            {closedDay ? "chiuso" : format(d, "MMM", { locale: it })}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="lg:col-span-3 bg-card border border-white/[0.08] rounded-lg p-6">
                  <div className="flex items-center gap-2 text-ember">
                    <Clock size={16} />
                    <span className="text-xs font-mono uppercase tracking-[0.25em]">3 · L'inizio</span>
                  </div>
                  <div data-testid="booking-slot-grid" className="mt-5 grid grid-cols-2 gap-2 max-h-[340px] overflow-y-auto no-scrollbar">
                    {loadingSlots ? (
                      <div className="col-span-2 flex justify-center py-10">
                        <Loader2 className="animate-spin text-ember" size={22} />
                      </div>
                    ) : closed ? (
                      <p className="col-span-2 text-sm text-stone py-8 text-center">La bottega è chiusa in questo giorno.</p>
                    ) : slots.length === 0 ? (
                      <p className="col-span-2 text-sm text-stone py-8 text-center">Nessun orario libero per questo servizio: prova un altro giorno.</p>
                    ) : (
                      slots.map((s) => (
                        <button
                          key={s.time}
                          data-testid={`booking-slot-item-${s.time.replace(":", "")}`}
                          onClick={() => setSelectedTime(s.time)}
                          className={`rounded-md border py-2.5 font-mono text-sm tracking-wider transition-all duration-300 ${
                            selectedTime === s.time
                              ? "bg-ember border-ember text-white shadow-[0_0_18px_rgba(230,90,30,0.35)]"
                              : "border-white/10 text-sand hover:border-ember/50 hover:text-ivory"
                          }`}
                        >
                          {s.time}
                        </button>
                      ))
                    )}
                  </div>
                </div>

                <form onSubmit={submit} className="lg:col-span-5 bg-card border border-white/[0.08] rounded-lg p-6 flex flex-col">
                  <div className="flex items-center gap-2 text-ember">
                    <User size={16} />
                    <span className="text-xs font-mono uppercase tracking-[0.25em]">4 · I tuoi dati</span>
                  </div>

                  <div className="mt-5 grid sm:grid-cols-2 gap-3">
                    <input
                      data-testid="booking-input-name"
                      required
                      minLength={2}
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Nome e cognome *"
                      className="bg-espresso border border-white/10 focus:border-ember/60 rounded-md px-4 py-3 text-sm text-ivory placeholder:text-stone outline-none transition-colors duration-300"
                    />
                    <input
                      data-testid="booking-input-phone"
                      required
                      minLength={6}
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="Cellulare *"
                      className="bg-espresso border border-white/10 focus:border-ember/60 rounded-md px-4 py-3 text-sm text-ivory placeholder:text-stone outline-none transition-colors duration-300"
                    />
                  </div>
                  <input
                    data-testid="booking-input-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="Email * (per il promemoria)"
                    className="mt-3 bg-espresso border border-white/10 focus:border-ember/60 rounded-md px-4 py-3 text-sm text-ivory placeholder:text-stone outline-none transition-colors duration-300"
                  />
                  <textarea
                    data-testid="booking-input-notes"
                    rows={2}
                    maxLength={300}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Note per Daniele (facoltative)"
                    className="mt-3 bg-espresso border border-white/10 focus:border-ember/60 rounded-md px-4 py-3 text-sm text-ivory placeholder:text-stone outline-none resize-none transition-colors duration-300"
                  />

                  <div className="mt-4 flex items-center justify-between text-xs font-mono uppercase tracking-widest text-stone">
                    <span className="capitalize">
                      {format(selectedDay, "EEEE d MMMM", { locale: it })}
                      {selectedTime && service ? ` · ${selectedTime}–${endTime(selectedTime, service.duration)}` : " · —"}
                    </span>
                    <span>{service ? `${service.price} · ${service.duration} min` : ""}</span>
                  </div>

                  <button
                    data-testid="booking-submit-button"
                    type="submit"
                    disabled={submitting || !selectedTime}
                    className="mt-4 w-full py-4 bg-ember hover:bg-ember-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-mono uppercase tracking-widest rounded-sm transition-all duration-300 shadow-[0_0_28px_rgba(230,90,30,0.3)] flex items-center justify-center gap-2"
                  >
                    {submitting && <Loader2 size={16} className="animate-spin" />}
                    {submitting ? "Conferma in corso…" : "Conferma prenotazione"}
                  </button>
                  <p className="mt-3 text-[11px] text-stone text-center leading-relaxed">
                    Nessun pagamento online. Si paga in bottega.
                  </p>
                </form>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
